import React, { useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';

import { NONE_FILTER_VALUE, reportFilterSelection } from './filterSelection';
import { type Row, csv, download, format, number, text } from './liveModel';

export function ReportFilters({
  definitions,
  values,
  options,
  onChange,
}: {
  definitions: ReadonlyArray<readonly [string, string]>;
  values: Record<string, Array<string>>;
  options: Record<string, Array<string>>;
  onChange: (key: string, values: Array<string>) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', lg: 'repeat(4,minmax(0,1fr))' },
        gap: 1.5,
      }}
    >
      {definitions.map(([key, label]) => (
        <Stack key={key}>
          <Autocomplete
            multiple
            size="small"
            options={options[key] ?? []}
            value={values[key] ?? []}
            getOptionLabel={(option) => (option === NONE_FILTER_VALUE ? 'None' : option)}
            onChange={(_, v) => onChange(key, reportFilterSelection(values[key] ?? [], v))}
            renderInput={(p) => (
              <TextField {...p} label={label} placeholder={(values[key] ?? []).length ? '' : 'All'} />
            )}
          />
          <Stack direction="row">
            <Button size="small" onClick={() => onChange(key, [])}>
              All
            </Button>
            <Button size="small" onClick={() => onChange(key, [NONE_FILTER_VALUE])}>
              None
            </Button>
          </Stack>
        </Stack>
      ))}
    </Box>
  );
}
export function ReportTable({
  title,
  rows,
  columns,
  source,
  truncated = false,
  onSelect,
  selected = [],
  exportColumns,
  exportContext = {},
}: {
  title: string;
  rows: Array<Row>;
  columns: Array<{ key: string; label: string; numeric?: boolean; percent?: boolean }>;
  source: string;
  truncated?: boolean;
  onSelect?: (row: Row, additive: boolean) => void;
  selected?: Array<string>;
  exportColumns?: Array<string>;
  exportContext?: Record<string, unknown>;
}) {
  const [page, setPage] = useState(0),
    [sort, setSort] = useState(''),
    [ascending, setAscending] = useState(true);
  const sorted = [...rows].sort((a, b) => {
    if (!sort) {
      return 0;
    }
    const an = number(a[sort]),
      bn = number(b[sort]);
    const cmp =
      columns.find((c) => c.key === sort)?.numeric && an !== null && bn !== null
        ? an - bn
        : text(a[sort]).localeCompare(text(b[sort]));
    return ascending ? cmp : -cmp;
  });
  const safePage = Math.min(page, Math.max(0, Math.ceil(rows.length / 25) - 1));
  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
        <Typography component="h2" variant="h6">
          {title}
        </Typography>
        <Button
          disabled={truncated}
          onClick={() =>
            download(
              title.toLowerCase().replace(/\W+/g, '-') + '.csv',
              csv(
                sorted.map((row) => ({ ...exportContext, exported_at: new Date().toISOString(), ...row })),
                [...Object.keys(exportContext), 'exported_at', ...(exportColumns ?? columns.map((c) => c.key))],
              ),
            )
          }
        >
          Export CSV
        </Button>
      </Stack>
      {truncated && (
        <Alert severity="warning">
          The query reached its row limit. Narrow the filters or dates before exporting the complete scope.
        </Alert>
      )}
      {rows.length === 0 && <Alert severity="info">No rows match these filters.</Alert>}
      <TableContainer>
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.numeric ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={sort === c.key}
                    direction={ascending ? 'asc' : 'desc'}
                    onClick={() => {
                      setAscending(sort === c.key ? !ascending : true);
                      setSort(c.key);
                    }}
                  >
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.slice(safePage * 25, safePage * 25 + 25).map((r, i) => (
              <TableRow key={i} hover selected={selected.includes(text(r.owner_tuple))}>
                <>
                  {columns.map((c, ci) => (
                    <TableCell
                      key={c.key}
                      align={c.numeric ? 'right' : 'left'}
                      sx={{ fontVariantNumeric: 'tabular-nums', minWidth: c.numeric ? 100 : 180 }}
                    >
                      {ci === 0 && onSelect ? (
                        <Button size="small" onClick={(e) => onSelect(r, e.ctrlKey || e.metaKey || e.shiftKey)}>
                          {text(r[c.key])}
                        </Button>
                      ) : c.numeric ? (
                        format(r[c.key], c.percent)
                      ) : (
                        text(r[c.key])
                      )}
                    </TableCell>
                  ))}
                </>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={safePage}
        rowsPerPage={25}
        rowsPerPageOptions={[25]}
        onPageChange={(_, p) => setPage(p)}
      />
      <Typography variant="caption" sx={{ display: 'block', px: 2, pb: 2, overflowWrap: 'anywhere' }}>
        {source} · CSV retains numeric precision and blank missing values.
      </Typography>
    </Paper>
  );
}
